import React, { useEffect, useRef, useState } from 'react';
import './AiInsightsRail.scss';
import { useAxios } from 'hooks/useAxios';
import { getDataFromSingleEndpoint } from 'services/Torogoz/TogorozApi';
import { useStore as useGlobalStore } from 'store/useGlobalStore';
import { AiAnalysisData } from '../shared/types';
import AiSessionSummary from './AiSessionSummary';
import CustomerSentimentTimeline from './CustomerSentimentTimeline';

interface AiInsightsRailProps {
  sessionId: string | null;
  isExternal: boolean;
  onDataLoaded?: (data: AiAnalysisData) => void;
}

type SummaryVariant = 0 | 1;

const AiInsightsRail: React.FC<AiInsightsRailProps> = ({ sessionId, isExternal, onDataLoaded }) => {
  const { endpoints, dateFilter } = useGlobalStore((state) => ({
    endpoints: state.endpoints,
    dateFilter: state.dateFilter,
  }));
  const [analysisData, setAnalysisData] = useState<AiAnalysisData | null>(null);
  const [summaryVariant, setSummaryVariant] = useState<SummaryVariant>(0);
  const [isInitialRequest, setIsInitialRequest] = useState(Boolean(sessionId));
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerationError, setRegenerationError] = useState<string | null>(null);
  const requestedVariantRef = useRef<SummaryVariant>(0);
  const observedLoadingRef = useRef(false);
  const { response: analysis, axiosFetch, loading, error } = useAxios();

  const requestAnalysis = (variant: SummaryVariant) => {
    if (!sessionId) return;

    requestedVariantRef.current = variant;
    getDataFromSingleEndpoint(
      endpoints.getWidgetData,
      {
        filters: {
          session_id: sessionId,
          date_interval: dateFilter,
          summary_variant: variant,
        },
        source: 'chat',
        actionName: 'session-gpt-analysis',
        ...(isExternal
          ? { external: true }
          : { external: false, mockEndpoint: 'data-session-gpt-analysis' }),
      },
      axiosFetch
    );
  };

  useEffect(() => {
    if (!sessionId) {
      setAnalysisData(null);
      return;
    }

    setAnalysisData(null);
    setSummaryVariant(0);
    setIsInitialRequest(true);
    setIsRegenerating(false);
    setRegenerationError(null);
    requestAnalysis(0);
  }, [sessionId, isExternal]);

  useEffect(() => {
    if (analysis === undefined) return;

    setIsInitialRequest(false);
    setIsRegenerating(false);
    if (!analysis?.data) return;
    const data: AiAnalysisData = analysis.data;
    setAnalysisData(data);
    setSummaryVariant(data.gpt_summary_variant ?? requestedVariantRef.current);
    setRegenerationError(null);
    onDataLoaded?.(data);
  }, [analysis]);

  useEffect(() => {
    if (loading) {
      observedLoadingRef.current = true;
      return;
    }
    if (!observedLoadingRef.current) return;

    observedLoadingRef.current = false;
    setIsInitialRequest(false);
    setIsRegenerating(false);
  }, [loading]);

  useEffect(() => {
    if (!error) return;

    setIsInitialRequest(false);
    if (isRegenerating) {
      setRegenerationError('Unable to regenerate the summary. Please try again.');
      setIsRegenerating(false);
    }
  }, [error]);

  const handleRegenerate = () => {
    const nextVariant: SummaryVariant = summaryVariant === 0 ? 1 : 0;
    setRegenerationError(null);
    setIsRegenerating(true);
    requestAnalysis(nextVariant);
  };

  return (
    <aside className="ca-ai-rail" aria-label="AI session insights">
      <div className="ca-ai-rail__body">
        {!sessionId && (
          <div className="ca-ai-rail__empty">
            <span className="material-icons" aria-hidden="true">
              forum
            </span>
            Select a session to view insights
          </div>
        )}

        {sessionId && (isInitialRequest || loading) && !analysisData && (
          <div className="ca-ai-rail__loading" aria-label="Loading AI analysis">
            <div className="ca-ai-rail__skeleton ca-ai-rail__skeleton--summary" />
            <div className="ca-ai-rail__skeleton ca-ai-rail__skeleton--timeline" />
          </div>
        )}

        {sessionId && !isInitialRequest && !loading && error && !analysisData && (
          <div className="ca-ai-rail__error" role="alert">
            <span className="material-icons" aria-hidden="true">
              error_outline
            </span>
            Failed to load AI insights. Please try again later.
          </div>
        )}

        {sessionId && !isInitialRequest && !loading && !error && !analysisData && (
          <div className="ca-ai-rail__empty">
            <span className="material-icons" aria-hidden="true">
              info
            </span>
            No AI analysis available
          </div>
        )}

        {analysisData && (
          <div className="ca-ai-rail__content">
            <AiSessionSummary
              data={analysisData}
              isRegenerating={isRegenerating}
              regenerationError={regenerationError}
              onRegenerate={handleRegenerate}
            />
            <CustomerSentimentTimeline points={analysisData.customer_sentiment_timeline ?? []} />
          </div>
        )}
      </div>
    </aside>
  );
};

export default AiInsightsRail;
